// src/pages/admin/seleccionar-rol.tsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';

export default function SeleccionarRol() {
  const { user, loading } = useUser();
  const navigate = useNavigate();

  // F2-011: revalidar permiso de admin_comite contra Firestore antes de navegar
  const entrarComoAdminComite = async () => {
    if (!user) return;
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const q = query(
        collection(db, 'admin'),
        where('correo', '==', (user.correo || '').toLowerCase()),
        where('activo', '==', true)
      );
      const snap = await getDocs(q);
      const data = snap.docs[0]?.data();
      const autorizado = !!data && Array.isArray(data.tipo) && data.tipo.includes('admin_comite');
      if (!autorizado) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso no autorizado',
          text: 'Tu cuenta ya no tiene permisos de admin de comité. Contacta al administrador.',
          confirmButtonColor: '#d33'
        });
        return;
      }
      sessionStorage.setItem('rolElegido', 'admin_comite');
      navigate('/admin-comite/dashboard');
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo verificar el acceso',
        text: 'Intenta de nuevo en unos segundos.',
        confirmButtonColor: '#d33'
      });
    }
  };

  useEffect(() => {
    // Si no hay usuario, redirigir al home
    if (!loading && !user) {
      navigate('/');
      return;
    }

    // Si el usuario no tiene permisos de comité, redirigir al dashboard normal
    if (!loading && user && !user.esAdminComite) {
      navigate('/dashboard');
      return;
    }

    // Si el usuario es admin_comite "puro" (sin perfil de miembro), saltar el selector
    // y entrar directo al dashboard de comité
    if (!loading && user && user.rol === 'ADMIN_COMITE') {
      sessionStorage.setItem('rolElegido', 'admin_comite');
      navigate('/admin-comite/dashboard');
      return;
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (!user || !user.esAdminComite) {
    return null;
  }

  return (
    <>
      <UserNav />
      <div className="page-title" style={{ background: 'linear-gradient(135deg, #0c2339 0%, #1a4a7a 100%)' }}>
        <div className="container">
          <h2 className="ipt-title text-white">Bienvenido, {user.nombre}</h2>
          <span className="ipn-subtitle text-white-50">Selecciona cómo deseas acceder al sistema</span>
        </div>
      </div>

      <section className="gray-simple" style={{ minHeight: '60vh' }}>
        <div className="container">
          <div className="row justify-content-center pt-5">
            <div className="col-lg-10">
              <div className="text-center mb-5">
                <h3 className="fw-bold mb-3">¿Cómo deseas ingresar?</h3>
                <p className="text-muted">
                  Tienes acceso a dos áreas diferentes del sistema. Selecciona según la tarea que necesites realizar.
                </p>
              </div>

              <div className="row g-4 justify-content-center">
                {/* Opción 1: Acceso como Miembro */}
                <div className="col-md-5">
                  <div
                    className="card border-0 shadow-sm h-100 cursor-pointer hover-shadow"
                    onClick={() => {
                      sessionStorage.setItem('rolElegido', 'miembro');
                      navigate('/dashboard');
                    }}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
                    }}
                  >
                    <div className="card-body text-center p-5">
                      <div
                        className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto mb-4"
                        style={{ width: '100px', height: '100px' }}
                      >
                        <i className="fa-solid fa-user fa-3x text-primary"></i>
                      </div>
                      <h4 className="fw-bold mb-3">Acceder como Miembro</h4>
                      <p className="text-muted mb-4">
                        Accede a tu perfil, propiedades, cursos inscritos y gestiona tu información personal.
                      </p>
                      <ul className="list-unstyled text-start mb-4">
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Mi perfil y datos personales
                        </li>
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Mis propiedades asignadas
                        </li>
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Mis cursos y créditos
                        </li>
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Inscribirme a talleres
                        </li>
                      </ul>
                      <button className="btn btn-outline-primary btn-lg px-5">
                        <i className="fa-solid fa-arrow-right me-2"></i>
                        Entrar como Miembro
                      </button>
                    </div>
                  </div>
                </div>

                {/* Opción 2: Acceso como Admin de Comité */}
                <div className="col-md-5">
                  <div
                    className="card border-0 shadow-sm h-100"
                    onClick={entrarComoAdminComite}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease', border: '2px solid #0c2339 !important' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 10px 30px rgba(12,35,57,0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
                    }}
                  >
                    <div className="card-body text-center p-5">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                        style={{ width: '100px', height: '100px', backgroundColor: '#0c2339' }}
                      >
                        <i className="fa-solid fa-gear fa-3x text-white"></i>
                      </div>
                      <h4 className="fw-bold mb-3">Acceder como Admin de Comité</h4>
                      <p className="text-muted mb-4">
                        Gestiona talleres, eventos, inscritos y controla la asistencia de los participantes.
                      </p>
                      <ul className="list-unstyled text-start mb-4">
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Crear y editar talleres/eventos
                        </li>
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Ver lista de inscritos
                        </li>
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Escanear QR de asistencia
                        </li>
                        <li className="mb-2">
                          <i className="fa-solid fa-check text-success me-2"></i>
                          Exportar reportes a Excel
                        </li>
                      </ul>
                      <button className="btn btn-primary btn-lg px-5">
                        <i className="fa-solid fa-arrow-right me-2"></i>
                        Entrar como Admin
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Información adicional */}
              <div className="text-center mt-5">
                <p className="text-muted small">
                  <i className="fa-solid fa-info-circle me-1"></i>
                  Puedes cambiar entre roles en cualquier momento desde el menú de tu dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
